import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Modal, ScrollView, View } from 'react-native'

import { Pressable, Stack } from '@zet/design-system'

import { InputWithLabel } from '../inputWithLabel'
import { B14 } from '../typography'

interface OptionField {
  key?: string | number
  value: string
  displayValue: string
}

interface SelectDropDownProps {
  label?: string
  placeholder?: string
  onChange: (value: string) => void
  onBlur: () => void
  value?: string
  errorMessage?: string
  dropOptions?: any
  example: string
}

export const SelectDropDown: React.FC<SelectDropDownProps> = ({
  label = 'Select Dropdown',
  placeholder = 'Select',
  onChange,
  onBlur,
  value = '',
  errorMessage,
  dropOptions,
  example,
}) => {
  const [selectedDisplayValue, setSelectedDisplayValue] =
    useState<string>(value)
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false)
  const [dropdownLayout, setDropdownLayout] = useState({ x: 0, y: 0, width: 0 })
  const [hoveredOption, setHoveredOption] = useState<string | number | null>(
    null,
  )
  const inputRef = useRef<View>(null)

  const options: OptionField[] = useMemo(() => {
    if (!Array.isArray(dropOptions)) return []

    return dropOptions.flatMap((option) => option?.fields ?? option ?? [])
  }, [dropOptions])

  useEffect(() => {
    if (!value) {
      setSelectedDisplayValue('')
      return
    }

    const matched = options.find((opt) => {
      const val = opt?.value ?? ''
      const display = opt?.displayValue ?? ''

      return (
        val?.toLowerCase?.() === value?.toLowerCase?.() ||
        display?.toLowerCase?.() === value?.toLowerCase?.()
      )
    })

    setSelectedDisplayValue(
      matched?.displayValue ?? matched?.value ?? value ?? '',
    )
  }, [value, options])

  const toggleDropdown = useCallback(() => {
    if (!isDropdownOpen && inputRef.current) {
      inputRef.current.measureInWindow((x, y, width, height) => {
        setDropdownLayout({ x, y: y + height, width })
        setIsDropdownOpen(true)
      })
    } else {
      setIsDropdownOpen(false)
    }
  }, [isDropdownOpen])

  const closeDropdown = useCallback(() => {
    setIsDropdownOpen(false)
  }, [])

  const handleSelect = useCallback(
    (option: OptionField) => {
      const { value: optionValue, displayValue } = option
      setSelectedDisplayValue(displayValue ?? optionValue)
      onChange(optionValue ?? displayValue ?? '')
      closeDropdown()
      onBlur()
    },
    [closeDropdown, onBlur, onChange],
  )

  return (
    <>
      <Stack className="mb-3" ref={inputRef}>
        <Pressable className="-mb-4" onPress={toggleDropdown}>
          <InputWithLabel
            example={example}
            onChange={() => null}
            isEditable={false}
            placeholder={placeholder}
            onBlur={onBlur}
            iconRight={isDropdownOpen ? 'ArrowUp' : 'ArrowDown'}
            iconLeft={'WorkOutlined'}
            value={selectedDisplayValue}
            label={label}
            errorMessage={errorMessage}
            pointerEvents="none"
            isFocused={isDropdownOpen}
          />
        </Pressable>
      </Stack>

      <Modal
        visible={isDropdownOpen}
        transparent
        animationType="none"
        onRequestClose={closeDropdown}
      >
        <Pressable className="flex-1 top-4" onPress={closeDropdown}>
          <Stack
            style={{
              position: 'absolute',
              top: dropdownLayout.y,
              left: dropdownLayout.x,
              width: dropdownLayout.width,
            }}
            className="rounded-xl border border-grays-40 bg-white shadow-lg"
          >
            <ScrollView
              keyboardShouldPersistTaps="handled"
              nestedScrollEnabled
              style={{ maxHeight: 220 }}
              contentContainerStyle={{ paddingVertical: 4 }}
            >
              {options.length ? (
                options.map((option) => {
                  const key =
                    option?.key ?? option?.value ?? option?.displayValue ?? ''
                  const isSelected =
                    selectedDisplayValue?.toLowerCase?.() ===
                      option?.displayValue?.toLowerCase?.() ||
                    selectedDisplayValue?.toLowerCase?.() ===
                      option?.value?.toLowerCase?.()

                  return (
                    <Pressable
                      key={key}
                      className={`px-3 py-3 ${
                        isSelected
                          ? 'bg-secondary-50'
                          : hoveredOption === key
                            ? 'bg-secondary-10'
                            : 'bg-transparent hover:bg-secondary-10'
                      }`}
                      onPress={() => handleSelect(option)}
                    >
                      <B14 className="font-semibold text-grays-100">
                        {option?.displayValue ?? option?.value}
                      </B14>
                    </Pressable>
                  )
                })
              ) : (
                <Stack className="px-3 py-3">
                  <B14 className="text-grays-60">No options found</B14>
                </Stack>
              )}
            </ScrollView>
          </Stack>
        </Pressable>
      </Modal>
    </>
  )
}
